'use client'

/**
 * Canonical client acceptance/decline path for Connect pending actions.
 * Used by Resume page and Circle Review Connection (via transport).
 */

import { clearConnectHandoffPayload } from '@/lib/play/connectResumeClientStorage'
import { trackConnectHandoffEvent } from '@/lib/play/connectHandoffAnalytics'
import type { ConnectHandoffCompletionCode } from '@/lib/play/connectHandoffTypes'
import { CONNECT_HANDOFF_COMPLETION_COPY } from '@/lib/play/connectHandoffCompletionCopy'
import {
  canActOnConnectResume,
  connectResumeSelfConnectionMessage,
} from '@/lib/play/connectResumeActionGate'

export type ConnectPendingActionResult =
  | { ok: true }
  | { ok: false; code?: string; message?: string }

export type ConnectAcceptSelectionDog = {
  id: string
  name: string
  breed?: string
  photoUrl?: string | null
}

export type ConnectAcceptSelectionInput = {
  eligibleDogs: ConnectAcceptSelectionDog[]
  /** Prefer preselecting this dog when eligible (e.g. Play active dog). */
  preferredDogId?: string | null
  blockReason?: 'SELF_CONNECTION' | 'NO_ELIGIBLE_DOGS' | null
  availableActions?: string[]
  senderDogName?: string | null
}

export type ConnectAcceptSelectionState = {
  selectableDogs: ConnectAcceptSelectionDog[]
  /** Initial / suggested selection — null when user must choose. */
  initialDogId: string | null
  requiresDogSelection: boolean
  blockReason: 'SELF_CONNECTION' | 'NO_ELIGIBLE_DOGS' | null
  blockMessage: string | null
}

export function resolveConnectAcceptSelection(
  input: ConnectAcceptSelectionInput
): ConnectAcceptSelectionState {
  if (input.blockReason === 'SELF_CONNECTION') {
    return {
      selectableDogs: [],
      initialDogId: null,
      requiresDogSelection: false,
      blockReason: 'SELF_CONNECTION',
      blockMessage: connectResumeSelfConnectionMessage(input.senderDogName),
    }
  }

  const selectableDogs = input.eligibleDogs ?? []
  if (selectableDogs.length === 0 || input.blockReason === 'NO_ELIGIBLE_DOGS') {
    return {
      selectableDogs: [],
      initialDogId: null,
      requiresDogSelection: false,
      blockReason: 'NO_ELIGIBLE_DOGS',
      blockMessage:
        'No eligible dog can accept this invitation yet. Finish a Play-ready dog profile, or switch to a dog that is not already connected.',
    }
  }

  const preferred =
    input.preferredDogId &&
    selectableDogs.some((dog) => dog.id === input.preferredDogId)
      ? input.preferredDogId
      : null

  return {
    selectableDogs,
    initialDogId: preferred ?? (selectableDogs.length === 1 ? selectableDogs[0]!.id : null),
    requiresDogSelection: selectableDogs.length > 1,
    blockReason: null,
    blockMessage: null,
  }
}

export function canActOnConnectAccept(
  selection: ConnectAcceptSelectionState,
  selectedDogId: string | null | undefined,
  availableActions: string[] = ['ACCEPT', 'DECLINE']
): boolean {
  if (selection.blockReason) return false
  return canActOnConnectResume(
    {
      availableActions,
      eligibleDogIds: selection.selectableDogs.map((dog) => dog.id),
      blockReason: null,
    },
    selectedDogId
  )
}

export function mapConnectAcceptErrorCode(
  code: string | undefined
): ConnectHandoffCompletionCode {
  if (code === 'NOT_PLAY_READY') return 'NOT_PLAY_READY'
  if (code === 'ALREADY_CONNECTED') return 'ALREADY_CONNECTED'
  if (
    code === 'SELF_CONNECTION' ||
    code === 'INITIATOR_CANNOT_ACCEPT' ||
    code === 'SELF_DOG_PAIR'
  ) {
    return 'SELF_CONNECTION'
  }
  return 'CONNECTION_NO_LONGER_AVAILABLE'
}

export function formatConnectAcceptedMessage(
  senderDogName: string,
  recipientDogName: string
): string {
  const sender = senderDogName.trim() || 'Their dog'
  const recipient = recipientDogName.trim() || 'your dog'
  return `${sender} and ${recipient} are now connected.`
}

export function connectAcceptErrorMessage(
  code: string | undefined,
  options?: { senderDogName?: string | null; fallback?: string }
): string {
  const mapped = mapConnectAcceptErrorCode(code)
  if (mapped === 'SELF_CONNECTION') {
    return connectResumeSelfConnectionMessage(options?.senderDogName)
  }
  return options?.fallback ?? CONNECT_HANDOFF_COMPLETION_COPY[mapped].message
}

/**
 * Canonical Accept: POST /api/play/pending/:connectionRef/accept
 * Body requires recipientDogId. Auth via session cookie.
 */
export async function acceptConnectPendingAction(
  connectionRef: string,
  recipientDogId: string,
  options?: { clearHandoff?: boolean }
): Promise<ConnectPendingActionResult> {
  const response = await fetch(
    `/api/play/pending/${encodeURIComponent(connectionRef)}/accept`,
    {
      method: 'POST',
      credentials: 'include',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ recipientDogId }),
    }
  )
  const json = (await response.json().catch(() => null)) as {
    success?: boolean
    code?: string
    error?: string
  } | null
  if (!response.ok || !json?.success) {
    return { ok: false, code: json?.code, message: json?.error }
  }
  if (options?.clearHandoff !== false) {
    await clearConnectHandoffPayload()
  }
  trackConnectHandoffEvent('play_connect_completed', { outcome: 'accepted' })
  return { ok: true }
}

/**
 * Canonical Decline: POST /api/play/pending/:connectionRef/decline
 */
export async function declineConnectPendingAction(
  connectionRef: string,
  recipientDogId: string,
  options?: { clearHandoff?: boolean }
): Promise<ConnectPendingActionResult> {
  const response = await fetch(
    `/api/play/pending/${encodeURIComponent(connectionRef)}/decline`,
    {
      method: 'POST',
      credentials: 'include',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ recipientDogId }),
    }
  )
  const json = (await response.json().catch(() => null)) as {
    success?: boolean
    code?: string
    error?: string
  } | null
  if (!response.ok || !json?.success) {
    return { ok: false, code: json?.code, message: json?.error }
  }
  if (options?.clearHandoff !== false) {
    await clearConnectHandoffPayload()
  }
  trackConnectHandoffEvent('play_connect_completed', { outcome: 'declined' })
  return { ok: true }
}
